import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import api from '@/api';
import { Loader2, CheckCircle2, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

export default function RequestService() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [trucks, setTrucks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [refId, setRefId] = useState('');
  const [form, setForm] = useState({ truck_id: '', priority: 'medium', issue_description: '', location: '', notes: '' });

  useEffect(() => {
    api.get('/trucks').then(r => setTrucks(r.data)).catch(() => toast.error('Could not load units')).finally(() => setLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await api.post('/service-requests', form);
      setRefId(res.data.id?.slice(0, 8).toUpperCase());
      setSubmitted(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-24"><Loader2 className="text-[#3B82F6] animate-spin" size={24} /></div>;

  if (submitted) return (
    <div className="flex flex-col items-center justify-center py-20 text-center animate-fade-in" data-testid="request-submitted">
      <div className="w-20 h-20 rounded-sm bg-[#57EB31]/10 border-2 border-[#57EB31]/40 flex items-center justify-center mb-6">
        <CheckCircle2 size={40} className="text-[#57EB31]" />
      </div>
      <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">// REQUEST SUBMITTED</p>
      <h2 className="text-white font-['Manrope'] font-extrabold text-2xl mb-3">Request Received</h2>
      <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] mb-2">Our team has been notified and will contact you shortly.</p>
      <p className="font-['JetBrains_Mono'] text-[#3B82F6] text-lg font-bold mb-8">REF: {refId}</p>
      <button onClick={() => navigate('/portal/repairs')}
        className="px-6 py-2.5 bg-[#3B82F6] text-white rounded-sm font-['Manrope'] font-bold text-sm uppercase tracking-wide hover:bg-[#2563EB] transition-colors">
        View My Repairs
      </button>
    </div>
  );

  const PRIORITY_OPTS = [
    { value: 'low', label: 'Low — Scheduled maintenance', color: 'text-[#5C6A7A]' },
    { value: 'medium', label: 'Medium — Needs attention soon', color: 'text-[#3B82F6]' },
    { value: 'high', label: 'High — Affecting operations', color: 'text-orange-400' },
    { value: 'emergency', label: 'Emergency — Unit down / safety issue', color: 'text-red-400' },
  ];

  return (
    <div className="max-w-2xl space-y-6 animate-fade-in" data-testid="request-service-page">
      <div className="flex items-center gap-4">
        <button onClick={() => navigate('/portal/dashboard')} className="text-[#5C6A7A] hover:text-[#3B82F6] transition-colors">
          <ArrowLeft size={18} />
        </button>
        <div>
          <p className="text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-1">// CLIENT PORTAL</p>
          <h1 className="text-white font-['Manrope'] font-extrabold text-2xl">Request New Service</h1>
        </div>
      </div>

      <div className="card-industrial p-6">
        <p className="text-[#B8C0CC] font-['IBM_Plex_Sans'] text-sm mb-6">
          Submit a service request and our operations team will review it and reach out to schedule.
        </p>
        <form onSubmit={handleSubmit} className="space-y-5">
          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">Select Unit *</label>
            <select value={form.truck_id} onChange={e => setForm(p => ({...p, truck_id: e.target.value}))} required
              className="w-full h-11 px-4 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors"
              data-testid="request-truck-select">
              <option value="">Choose your unit...</option>
              {trucks.map(t => <option key={t.id} value={t.id}>{t.unit_number} — {t.year} {t.make} {t.model}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">Priority Level *</label>
            <div className="grid grid-cols-2 gap-2">
              {PRIORITY_OPTS.map(({ value, label, color }) => (
                <button key={value} type="button" onClick={() => setForm(p => ({...p, priority: value}))}
                  className={`p-3 rounded-sm border text-left transition-all ${form.priority === value ? 'border-[#3B82F6] bg-[#3B82F6]/8' : 'border-[#2D3F5E] hover:border-[#3B82F6]/40'}`}
                  data-testid={`priority-${value}`}>
                  <p className={`text-xs font-bold font-['Manrope'] ${color}`}>{value.toUpperCase()}</p>
                  <p className="text-[10px] text-[#5C6A7A] font-['IBM_Plex_Sans'] mt-0.5">{label.split(' — ')[1]}</p>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">Issue Description *</label>
            <textarea value={form.issue_description} onChange={e => setForm(p => ({...p, issue_description: e.target.value}))} required rows={3}
              className="w-full px-4 py-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors resize-none"
              placeholder="Describe what's happening with the unit..." data-testid="request-description" />
          </div>

          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">Current Location *</label>
            <input type="text" value={form.location} onChange={e => setForm(p => ({...p, location: e.target.value}))} required
              className="w-full h-11 px-4 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors"
              placeholder="City, State" data-testid="request-location" />
          </div>

          <div>
            <label className="block text-[9px] font-['JetBrains_Mono'] uppercase tracking-[0.15em] text-[#5C6A7A] mb-2">Additional Notes</label>
            <textarea value={form.notes} onChange={e => setForm(p => ({...p, notes: e.target.value}))} rows={2}
              className="w-full px-4 py-3 bg-[#0A0F1A] border border-[#2D3F5E] rounded-sm text-white font-['IBM_Plex_Sans'] text-sm focus:outline-none focus:border-[#3B82F6] transition-colors resize-none"
              placeholder="Any other details..." />
          </div>

          <button type="submit" disabled={submitting} data-testid="request-submit-btn"
            className="w-full h-12 bg-[#3B82F6] text-white font-['Manrope'] font-bold text-sm uppercase tracking-wider rounded-sm hover:bg-[#2563EB] transition-colors disabled:opacity-50 shadow-[0_0_20px_rgba(59,130,246,0.2)] flex items-center justify-center gap-2">
            {submitting && <Loader2 size={16} className="animate-spin" />}
            {submitting ? 'Submitting...' : 'Submit Service Request'}
          </button>
        </form>
      </div>
    </div>
  );
}
